import type { GameVersion, RomPatch } from "@redchain/contracts";

/** wLinkState (pokered). Non-zero means a Cable Club room, trade, or link battle. */
const W_LINK_STATE = 0xd12b;
/**
 * Scratch WRAM, outside the saved main-data block (`wMainDataStart` is `0xd2f7`).
 * The patch-10 receptionist sets it before SaveGameData and clears it on every exit and on load.
 */
export const W_REDCHAIN_CABLE_CLUB = 0xcc39;

const POLL_MS = 2_000;
const POLL_MAX_MS = 30_000;

/** Game-save detection runs outside a lockstep segment. The trade's in-link save is not a checkpoint. */
export function watchGameSaves(linkStepping: boolean): boolean {
  return !linkStepping;
}

/**
 * The armed lobby binds when a link room is already running, or when the receptionist marker
 * is set and this step's PC watch hit the patch-10 connection-loop entry. A Start-menu save
 * does not, even if an older file was written while the marker was set: the marker is not saved.
 */
export function isCableClubLinkSave(
  readWram: (addr: number) => number,
  _readHram: (addr: number) => number,
  loopEntry: boolean,
): boolean {
  if (readWram(W_LINK_STATE) !== 0) return true;
  return loopEntry && readWram(W_REDCHAIN_CABLE_CLUB) !== 0;
}

/** Null when this lockstep opponent uses our game and our ROM patch. Otherwise a player-facing reason. */
export function opponentPatchRefusal(
  ownVersion: GameVersion,
  ownPatch: RomPatch,
  status: { opponentGameVersion: GameVersion | null; opponentRomPatch: RomPatch | null },
): string | null {
  if (!status.opponentGameVersion || status.opponentGameVersion !== ownVersion) {
    return "Your opponent is playing a different game version. You both need the same Pokémon Red or Blue to link.";
  }
  const theirs = status.opponentRomPatch;
  if (!theirs || theirs.version !== ownPatch.version || theirs.rcdPerSol !== ownPatch.rcdPerSol) {
    return "Your opponent is on a different game patch. Reload so you both run the same version.";
  }
  return null;
}

/** Delay before the next lobby poll. The first failure waits `POLL_MS`, then the wait doubles. */
export function nextLobbyPollDelay(consecutiveFailures: number): number {
  if (consecutiveFailures <= 0) return POLL_MS;
  return Math.min(POLL_MS * 2 ** (consecutiveFailures - 1), POLL_MAX_MS);
}
